import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { Hint } from '../../components/ui/Hint';
import { listTypes } from '../components/api';
import { downloadBlob } from '../labels/api';
import { QuestionList } from './QuestionList';
import {
  analyseChunk,
  answerAll,
  cancelImport,
  commitChunk,
  downloadRework,
  downloadTemplate,
  listImports,
  listQuestions,
  readProgress,
  retryFailed,
  sendAnswers,
  startImport,
  type Answer,
  type Progress,
  type Question,
  type QuestionKind,
} from './api';

/** Three attempts with a growing pause. A chunk that fails is retried rather
 *  than restarted: the rows it already finished are no longer in the state the
 *  next call selects, so repeating it is safe and picks up only what is left. */
async function withRetries<T>(step: () => Promise<T>): Promise<T> {
  let last: unknown;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      return await step();
    } catch (error) {
      last = error;
      await new Promise((resolve) => window.setTimeout(resolve, 500 * (attempt + 1)));
    }
  }
  throw last;
}

/**
 * Importing a catalogue and its opening stock from an old database.
 *
 * The work is done in chunks because there is no worker tier and three thousand
 * rows do not fit in one request. The loops live here rather than on the server
 * for the same reason: the client is the thing that can afford to wait, and it
 * is the thing that can show how far along the wait is.
 *
 * Nothing is created until the questions have been answered. That ordering is
 * the whole design — a bulk import that guesses is the fastest way to build the
 * catalogue of duplicates the third phase was written to prevent.
 */
export function ImportsPage() {
  const { t, i18n } = useTranslation();
  const [typeId, setTypeId] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const types = useQuery({ queryKey: ['reference', 'types', 'all'], queryFn: () => listTypes('all') });
  const sessions = useQuery({ queryKey: ['imports'], queryFn: listImports });

  const fail = (exc: unknown) => setError(exc instanceof ApiError ? exc.code : 'unknown');

  const refreshQuestions = useCallback(async (id: string) => {
    setQuestions(await listQuestions(id));
  }, []);

  /** Run one chunked phase to the end, then reload what is left to decide. */
  const drain = useCallback(
    async (
      id: string,
      step: (id: string) => Promise<Progress>,
      left: (state: Progress) => number,
    ) => {
      let state = await withRetries(() => step(id));
      setProgress(state);
      while (left(state) > 0) {
        state = await withRetries(() => step(id));
        setProgress(state);
      }
      await refreshQuestions(id);
      return state;
    },
    [refreshQuestions],
  );

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (exc) {
      fail(exc);
    } finally {
      setBusy(false);
    }
  };

  const onTemplate = () =>
    run(async () => {
      const blob = await downloadTemplate(typeId, i18n.language === 'en' ? 'en' : 'ru');
      downloadBlob(blob, 'storageapp-import.xlsx');
    });

  const onStart = () =>
    run(async () => {
      if (!file) return;
      const started = await startImport(file, typeId, i18n.language === 'en' ? 'en' : 'ru');
      setProgress(started);
      await drain(started.session_id, analyseChunk, (state) => state.remaining_analyse);
      await sessions.refetch();
    });

  const onAnswer = (answers: Answer[]) =>
    run(async () => {
      if (!progress) return;
      const id = progress.session_id;
      setProgress(await sendAnswers(id, answers));
      // Answering re-opens analysis: a package is part of a part's identity, so
      // the rows that used it are read again rather than patched in place.
      await drain(id, analyseChunk, (state) => state.remaining_analyse);
    });

  const onAnswerAll = (kind: QuestionKind, action: 'use' | 'skip') =>
    run(async () => {
      if (!progress) return;
      const id = progress.session_id;
      setProgress(await answerAll(id, kind, action));
      await drain(id, analyseChunk, (state) => state.remaining_analyse);
    });

  const onCommit = () =>
    run(async () => {
      if (!progress) return;
      await drain(progress.session_id, commitChunk, (state) => state.remaining_commit);
    });

  const onRework = () =>
    run(async () => {
      if (!progress) return;
      const blob = await downloadRework(progress.session_id);
      downloadBlob(blob, `${progress.code}-rework.xlsx`);
    });

  const onRetry = () =>
    run(async () => {
      if (!progress) return;
      setProgress(await retryFailed(progress.session_id));
    });

  const onCancel = () =>
    run(async () => {
      if (!progress) return;
      setProgress(await cancelImport(progress.session_id));
      await sessions.refetch();
    });

  const onResume = (id: string) =>
    run(async () => {
      setProgress(await readProgress(id));
      await refreshQuestions(id);
    });

  const count = (state: string) => progress?.counts[state] ?? 0;

  return (
    // A page, not a card. It was both, and the blocks inside it are cards of
    // their own — a card inside a card inside a card is why this screen read as
    // fussy rather than as three steps.
    <section className="imports">
      <div className="imports__intro">
        <h2>{t('imports.title')}</h2>
        <p className="hint">{t('imports.intro')}</p>
      </div>

      {error && (
        <p className="error">
          {t(`imports.errors.${error}`, { defaultValue: t('imports.errors.unknown') })}
        </p>
      )}

      <div className="imports__setup">
        {/* Needed to *make* a template, not to load one back: a template
            generated here says which type it is for, and asking again is a
            question whose answer is already in the box. */}
        <label>
          <span className="component-form__label">
            {t('imports.type')}
            <Hint text={t('imports.typeHint')} />
          </span>
          <select value={typeId} onChange={(event) => setTypeId(event.target.value)}>
            <option value="">{t('common.notChosen')}</option>
            {(types.data ?? []).map((type) => (
              <option key={type.id} value={type.id}>
                {i18n.language === 'en' ? type.name_en : type.name_ru}
              </option>
            ))}
          </select>
        </label>

        <button type="button" disabled={!typeId || busy} onClick={onTemplate}>
          {t('imports.downloadTemplate')}
        </button>

        {/* Shaped like the field beside it — caption above, control below —
            rather than a caption with a native file box trailing after it. */}
        <label>
          <span className="component-form__label">{t('imports.file')}</span>
          <input
            type="file"
            accept=".xlsx,.xlsm,.csv"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>

        <button type="button" disabled={!file || busy} onClick={onStart}>
          {t('imports.start')}
        </button>
      </div>

      {progress && (
        <div className="imports__progress">
          <h3>
            {progress.code}
            {' — '}
            {t(`imports.statuses.${progress.status}`)}
          </h3>

          {/* What the run was understood to be about. Worth saying out loud
              precisely because nobody chose it: the file did. */}
          <p className="hint">
            {t('imports.readAs', {
              type:
                (types.data ?? []).find((item) => item.id === progress.component_type_id)?.[
                  i18n.language === 'en' ? 'name_en' : 'name_ru'
                ] ?? progress.component_type_id,
            })}
          </p>

          {progress.identity_blind && (
            <p className="imports__alarm">{t('imports.identityBlind')}</p>
          )}

          <ul className="imports__counts">
            <li>{t('imports.counts.total', { count: progress.total })}</li>
            <li>{t('imports.counts.ready', { count: count('ready') })}</li>
            <li>{t('imports.counts.needsInput', { count: progress.open_questions })}</li>
            <li>{t('imports.counts.invalid', { count: count('invalid') })}</li>
            <li>{t('imports.counts.committed', { count: count('committed') })}</li>
            <li>{t('imports.counts.skipped', { count: count('skipped') })}</li>
            <li>{t('imports.counts.failed', { count: count('failed') })}</li>
          </ul>

          <div className="imports__actions">
            <button
              type="button"
              disabled={busy || progress.remaining_commit === 0}
              onClick={onCommit}
            >
              {t('imports.commit', { count: progress.remaining_commit })}
            </button>
            <button type="button" disabled={busy || count('failed') === 0} onClick={onRetry}>
              {t('imports.retry')}
            </button>
            {/* Everything that did not go in, as the same spreadsheet plus a
                reason — correcting it and loading it again is the same tool. */}
            <button type="button" className="link" disabled={busy} onClick={onRework}>
              {t('imports.rework')}
            </button>
            <button
              type="button"
              className="link"
              disabled={busy || progress.status !== 'open'}
              onClick={onCancel}
            >
              {t('imports.cancel')}
            </button>
          </div>
        </div>
      )}

      {progress && (
        <QuestionList
          questions={questions}
          busy={busy}
          onAnswer={onAnswer}
          onAnswerAll={onAnswerAll}
        />
      )}

      <section className="panel">
        <div className="panel__section-head">
          <h3>{t('imports.previous')}</h3>
        </div>
        {/* Its own scroll box: the file names run long and the dates must not be
            cut off by the card holding them. */}
        <div className="scroll-x">
        <table className="table">
          <thead>
            <tr>
              <th>{t('imports.code')}</th>
              <th>{t('imports.fileName')}</th>
              <th>{t('imports.status')}</th>
              <th>{t('imports.started')}</th>
            </tr>
          </thead>
          <tbody>
            {(sessions.data ?? []).map((item) => (
              <tr key={item.id}>
                <td>
                  <button type="button" className="link" onClick={() => onResume(item.id)}>
                    {item.code}
                  </button>
                </td>
                <td>{item.filename ?? '—'}</td>
                <td>{t(`imports.statuses.${item.status}`)}</td>
                <td>{new Date(item.created_at).toLocaleString(i18n.language)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
    </section>
  );
}
